import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import SignatureCanvas, { stripSignatureDataUrl } from '@/components/ui/SignatureCanvas/SignatureCanvas';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import { SignSupportRequestDtoIn } from '@/models/in/SignSupportRequestDtoIn';
import { SupportRequestSignaturesStatusDtoOut } from '@/models/out/SupportRequestSignatureDtoOut';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import useFundContext from '@/contexts/Fund/useFundContext';
import { formatDateLocale } from '@/helpers/dateHelper';
import { getRepresentativeActionErrorMessage } from '@/helpers/apiErrorHelper';
import styles from './SignSupportRequestDialog.module.css';

interface SignSupportRequestDialogProps {
  open: boolean;
  onClose: () => void;
  request: SupportRequestDtoIn | null;
  onSuccess: () => void;
}

const SignSupportRequestDialog = ({ open, onClose, request, onSuccess }: SignSupportRequestDialogProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();

  const [signature, setSignature] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SupportRequestSignaturesStatusDtoOut | null>(null);

  const { mutate: signRequest, isPending: isSubmitting } = usePostAuthApi<SupportRequestSignaturesStatusDtoOut, SignSupportRequestDtoIn>(
    `funds/${currentFund?.id}/support-requests/${request?.correlationId}/signatures`
  );

  useEffect(() => {
    if (open) {
      setSignature('');
      setError(null);
      setResult(null);
    }
  }, [open]);

  if (!request) return null;

  const handleSubmit = () => {
    if (!currentFund?.id || !signature) return;
    setError(null);

    const body: SignSupportRequestDtoIn = {
      signature: {
        name: 'signature.png',
        description: 'representative signature',
        contentBase64: stripSignatureDataUrl(signature),
      },
    };

    signRequest(body, {
      onSuccess: (status) => {
        setResult(status);
        onSuccess();
      },
      onError: (err) => {
        setError(getRepresentativeActionErrorMessage(err, t, 'signSupportRequest.error'));
      },
    });
  };

  const renderResult = (status: SupportRequestSignaturesStatusDtoOut) => (
    <div className={styles.successContent}>
      <p className={styles.successMessage}>
        {status.wasSentToOffice ? t('signSupportRequest.sentToOffice') : t('signSupportRequest.waitingForSecondSignature')}
      </p>
      <div className={styles.actions}>
        <Button variant="primary" className={styles.actionButton} onClick={onClose}>
          {t('signSupportRequest.close')}
        </Button>
      </div>
    </div>
  );

  const renderForm = () => (
    <>
      <p className={styles.confirmText}>{t('signSupportRequest.confirmText')}</p>

      <SignatureCanvas onCapture={setSignature} invalid={!!error && !signature} />

      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.actions}>
        <Button variant="white" className={styles.secondaryButton} onClick={onClose} disabled={isSubmitting}>
          {t('signSupportRequest.cancel')}
        </Button>
        <Button variant="primary" className={styles.actionButton} onClick={handleSubmit} disabled={isSubmitting || !signature}>
          {isSubmitting ? t('loading') : t('signSupportRequest.confirm')}
        </Button>
      </div>
    </>
  );

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) onClose();
      }}
      title={t('signSupportRequest.title')}
    >
      <div className={styles.content}>
        {request.requestedAt && <p className={styles.subtitle}>{t('signSupportRequest.requestedAt', { date: formatDateLocale(request.requestedAt) })}</p>}
        {result ? renderResult(result) : renderForm()}
      </div>
    </MainDialogLayout>
  );
};

export default SignSupportRequestDialog;
